"use client";

import { Icon } from "@repo/ui/components/icon";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@repo/ui/input-group";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/tabs";
import { Button } from "@repo/ui/ui/button";
import { AnimatePresence, LayoutGroup, motion } from "motion/react";
import { type ChangeEvent, type ReactNode, useCallback, useState } from "react";

import { AvatarPickerProvider, useAvatarPicker } from "./avatar-picker.context";
import type { AvatarPickerProps } from "./avatar-picker.types";
import { Icons } from "./custom-icons";
import { Emojis } from "./emoji-list";

// --- Inner content (uses context) ---

const AvatarContent = () => {
  const { searchQuery, setSearchQuery, clearAvatar } = useAvatarPicker();

  // Tabs must be controlled: opening search unmounts the triggers, and an
  // uncontrolled Base UI Tabs root resets its value to null when no tabs are
  // registered, which would unmount the active panel and empty the body.
  const [activeTab, setActiveTab] = useState("custom-icons");

  const isSearchOpen = searchQuery !== null;

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value),
    [setSearchQuery],
  );

  return (
    <Tabs
      value={activeTab}
      onValueChange={(value) => setActiveTab(value as string)}
      className="h-full min-h-0 gap-0 overflow-hidden"
    >
      <TabsList
        variant="line"
        className="h-fit! w-full shrink-0 justify-start border-b p-2!"
      >
        <LayoutGroup>
          {/* Tab triggers — fade out when search opens */}
          <AnimatePresence initial={false}>
            {!isSearchOpen && (
              <motion.div
                key="tabs"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.15 }}
                className="flex items-center"
              >
                <TabsTrigger
                  value="custom-icons"
                  className="h-auto flex-none after:-bottom-3.5!"
                >
                  <Icon.Custom name="elements" />
                  Icons
                </TabsTrigger>
                <TabsTrigger
                  value="emojis"
                  className="h-auto flex-none after:-bottom-3.5!"
                >
                  <Icon.Custom name="emotion-smile" />
                  Emoji
                </TabsTrigger>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Search input — expands from the icon position */}
          <AnimatePresence initial={false}>
            {isSearchOpen && (
              <motion.div
                key="search"
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: "100%", opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="min-w-0 overflow-hidden"
              >
                <InputGroup className="border-none! ring-0!">
                  <InputGroupAddon>
                    <motion.div
                      layoutId="search-icon"
                      transition={{ duration: 0.2 }}
                    >
                      <Icon name="magnifying-glass" className="size-3.5" />
                    </motion.div>
                  </InputGroupAddon>
                  <InputGroupInput
                    autoFocus
                    value={searchQuery ?? ""}
                    onChange={handleSearchChange}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        e.stopPropagation();
                        setSearchQuery(null);
                      }
                    }}
                    placeholder="Search icons..."
                  />
                </InputGroup>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Right-side action buttons */}
          <div className="ml-auto flex items-center">
            <AnimatePresence initial={false}>
              {!isSearchOpen && (
                <motion.div
                  key="search-btn"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.1 }}
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSearchQuery("")}
                  >
                    <motion.div
                      layoutId="search-icon"
                      transition={{ duration: 0.2 }}
                    >
                      <Icon name="magnifying-glass" className="size-3.5" />
                    </motion.div>
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
            {isSearchOpen ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchQuery(null)}
              >
                <Icon name="cross" />
              </Button>
            ) : (
              <Button variant="ghost" size="icon" onClick={clearAvatar}>
                <Icon name="trash" />
              </Button>
            )}
          </div>
        </LayoutGroup>
      </TabsList>

      <TabsContent
        value="custom-icons"
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        <Icons />
      </TabsContent>
      <TabsContent
        value="emojis"
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        <Emojis />
      </TabsContent>
    </Tabs>
  );
};

// --- Popover wrapper ---

function AvatarPopover({
  trigger,
  defaultOpen = false,
}: {
  trigger: ReactNode;
  defaultOpen?: boolean;
}) {
  const { setSearchQuery } = useAvatarPicker();
  const [open, setOpen] = useState(defaultOpen);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen) setSearchQuery(null);
    },
    [setSearchQuery],
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger render={trigger as React.JSX.Element} />
      <PopoverContent
        className="h-[395px] min-h-0 w-[305px] min-w-0 gap-0 overflow-hidden p-0"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <AvatarContent />
      </PopoverContent>
    </Popover>
  );
}

// --- Public API ---

export function AvatarPicker({
  value,
  onChange,
  children,
  defaultOpen,
}: AvatarPickerProps) {
  return (
    <AvatarPickerProvider value={value} onChange={onChange}>
      <AvatarPopover trigger={children} defaultOpen={defaultOpen} />
    </AvatarPickerProvider>
  );
}

export type { AvatarPickerProps, AvatarValue } from "./avatar-picker.types";
